// src/pages/Auth/AuthPage.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Form, Input, Button, Card, message } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import { useAuth } from "../../hooks/useBlogAPI";
import Arknights from "../../components/Arknights";
import "./AuthPage.css";

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleLogin = async (values) => {
    try {
      const credentials = {
        password: values.password,
      };

      if (values.identifier.includes("@")) {
        credentials.email = values.identifier;
      } else if (/^1[3-9]\d{9}$/.test(values.identifier)) {
        credentials.phone = values.identifier;
      } else {
        credentials.username = values.identifier;
      }

      await login(credentials);
      message.success("登录成功");
      navigate("/");
    } catch (error) {
      message.error(error.message || "登录失败，请稍后重试");
    }
  };

  const handleRegister = async (values) => {
    try {
      const userData = {
        username: values.username,
        email: values.email,
        phone: values.phone,
        password: values.password,
      };

      await register(userData);

      message.success("注册成功");
      navigate("/");
    } catch (error) {
      message.error(error.message || "注册失败，请稍后重试");
    }
  };

  const getParticleImage = () => {
    return isLogin
      ? "/LoginPhoto/longmen.png"
      : "/LoginPhoto/deepseahunter.png";
  };

  const particleOptions = {
    renderX: 100,
    renderY: 100,
    spacing: 1.5,
    size: 1.2,
    Drag: 0.9,
    Ease: 0.15,
    Thickness: 30,
    validColor: {
      min: 200,
      max: 765,
      invert: false,
    },
  };

  return (
    <div className="auth-page-container">
      <Arknights
        src={getParticleImage()}
        options={particleOptions}
        width="100%"
        height="100%"
      />
      <div className="auth-overlay">
        <div className="auth-form-container-right">
          <Card title={isLogin ? "用户登录" : "用户注册"} className="auth-card">
            {isLogin ? (
              <Form
                name="login_form"
                initialValues={{ remember: true }}
                onFinish={handleLogin}
              >
                <Form.Item
                  name="identifier"
                  rules={[
                    {
                      required: true,
                      message: "请输入用户名、手机号、邮箱号",
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名 / 手机号 / 邮箱号"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: "请输入密码" }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                    size="large"
                    visibilityToggle={{
                      visible: passwordVisible,
                      onVisibleChange: setPasswordVisible,
                    }}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" size="large" block>
                    登录
                  </Button>
                </Form.Item>

                <div style={{ textAlign: "center" }}>
                  没有账号？{" "}
                  <Link onClick={() => setIsLogin(false)}>立即注册</Link>
                </div>
              </Form>
            ) : (
              <Form name="register_form" onFinish={handleRegister}>
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: "请输入用户名" },
                    { min: 1, message: "用户名长度不能少于1个字符" },
                    { max: 20, message: "用户名长度不能超过20个字符" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "请输入邮箱地址" },
                    { type: "email", message: "请输入有效的邮箱地址" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="邮箱地址"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  rules={[
                    { required: true, message: "请输入手机号" },
                    {
                      pattern: /^1[3-9]\d{9}$/,
                      message: "请输入有效的手机号",
                    },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="手机号"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "密码至少6位" },
                    { min: 6, message: "请确认密码" },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                    size="large"
                    visibilityToggle={{
                      visible: passwordVisible,
                      onVisibleChange: setPasswordVisible,
                    }}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="confirm"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "请确认密码" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("两次输入的密码不一致")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="确认密码"
                    size="large"
                    visibilityToggle={{
                      visible: passwordVisible,
                      onVisibleChange: setPasswordVisible,
                    }}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" size="large" block>
                    注册
                  </Button>
                </Form.Item>

                <div style={{ textAlign: "center" }}>
                  已有账号？{" "}
                  <Link onClick={() => setIsLogin(true)}>立即登录</Link>
                </div>
              </Form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
